import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Retiene el foco dentro de un panel mientras esta abierto y lo devuelve al
 * cerrarlo.
 *
 * Sin esto, quien navega con teclado sale del panel deslizante en el primer
 * tabulador y sigue recorriendo la pagina que hay debajo, invisible bajo la
 * capa oscura, sin ninguna forma de saber donde esta el cursor.
 */
export function useFocusTrap(
  container: RefObject<HTMLElement | null>,
  isActive: boolean,
  onEscape: () => void,
): void {
  /*
   * `onEscape` se guarda en una referencia y **no** entra en las dependencias
   * del efecto.
   *
   * Quien abre el panel casi siempre pasa una función escrita en el sitio
   * —`onClose={() => { ... }}`—, y esa función es nueva en cada render. Con
   * ella en las dependencias, el efecto se desmontaba y se volvía a montar en
   * **cada pulsación de tecla**: la limpieza devolvía el foco al elemento
   * anterior y el montaje lo llevaba otra vez al primer campo del panel. El
   * síntoma era escribir en un formulario y ver que las letras se pierden o
   * saltan de campo, y parecía lentitud cuando era el foco moviéndose.
   *
   * Con la referencia, la trampa se monta una vez al abrir y se desmonta al
   * cerrar, que es cuando de verdad cambia algo, y el `Escape` sigue llamando
   * a la función más reciente.
   */
  const escapeRef = useRef(onEscape);

  useEffect(() => {
    escapeRef.current = onEscape;
  });

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const node = container.current;
    if (!node) {
      return;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusable = (): HTMLElement[] =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => element.offsetParent !== null,
      );

    focusable()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        escapeRef.current();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const elements = focusable();
      const first = elements[0];
      const last = elements[elements.length - 1];

      if (!first || !last) {
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, [container, isActive]);
}
